import { z } from 'zod'
import { orderResponseSchema } from './order-response.schema'
import { botSessionEventSchema } from './bot-debug.schema'
import { botAccountSettingsSchema } from './bot-account-settings.schema'
import { botInstrumentResponseSchema } from './bot-instrument.schema'

// the schema the account-level debug export is stamped with; bump when the shape changes so an
// offline reader (or the analyze-bot-data command) can detect and adapt to an older capsule.
export const BOT_ACCOUNT_DEBUG_SCHEMA_VERSION = 1

// a session event enriched for the account-level capsule. the per-instrument debug schema
// (botSessionEventSchema) carries no instrument attribution because it is always scoped to one
// instrument; the account capsule spans every instrument, so each event carries its own ticker +
// uid so it stays attributable offline. null when the instrument row is missing.
export const botAccountDebugEventSchema = botSessionEventSchema.extend({
  instrumentUid: z.string().nullable(),
  ticker: z.string().nullable(),
})
export type BotAccountDebugEventDto = z.infer<typeof botAccountDebugEventSchema>

export const botAccountDebugMetaSchema = z.object({
  schemaVersion: z.number().int(),
  bot: z.literal('auto-buy'),
  account: z.object({ connectionId: z.string(), accountId: z.string() }),
  exportedAt: z.coerce.date(),
  instrumentCount: z.number().int(),
  orderCount: z.number().int(),
  eventCount: z.number().int(),
})
export type BotAccountDebugMetaDto = z.infer<typeof botAccountDebugMetaSchema>

// a self-contained, account-level, all-time debug capsule for the auto-buy bot: the config, every
// tracked instrument, the full order ledger, and the complete event history — bundled for one-click
// download and offline analysis. deliberately parallel to the day-trade capsule shape so the same
// analyze-bot-data tooling can consume both. realized P&L is intentionally left off; it is derivable
// offline from the raw order ledger (FIFO), keeping the capsule a faithful record rather than a
// pre-computed view.
export const botAccountDebugResponseSchema = z.object({
  meta: botAccountDebugMetaSchema,
  settings: botAccountSettingsSchema.nullable(),
  instruments: z.array(botInstrumentResponseSchema),
  orders: z.array(orderResponseSchema),
  events: z.array(botAccountDebugEventSchema),
})
export type BotAccountDebugResponseDto = z.infer<typeof botAccountDebugResponseSchema>
